import { closestCenter, DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, UniqueIdentifier, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { findIndex } from 'lodash';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { FB } from '..';
import { translate } from '../../../common/intl';
import Loader from '../../components/common/kendo/Loader';
import { useDeferredRender } from '../FBDeferredRender/FBDeferredRender.context';
import { SortablePlaceholder } from './components/SortablePlaceholder';
import { FBSortableProps } from './types';
import { withFBSortable } from './wrap';

const SortableItemLazy = lazy(async () => await import('./components/SortableItem'));

const FBSortable: React.FC<FBSortableProps> = ({
  items = [],
  updateWorkspaceSchema,
}) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier>();
  const [dragSize, setDragSize] = useState<{ width: number, height: number }>();
  const [localItems, setLocalItems] = useState(items);
  const scheduler = useDeferredRender()?.scheduler;
  const { workspaceState } = FB.useStores();
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(
    () => setLocalItems(items),
    [items],
  );

  useEffect(() => () => scheduler?.setPaused(false), [scheduler]);

  function finishDrag () {
    setActiveId(undefined);
    setDragSize(undefined);
    scheduler?.setPaused(false);
  }

  function updateLocalSchema (oldId: string, newId: string) {
    const oldIndex = findIndex(localItems, { name: oldId });
    const newIndex = findIndex(localItems, { name: newId });
    setLocalItems((prev) => arrayMove(prev, oldIndex, newIndex));
  }

  function handleDragEnd (event: DragEndEvent) {
    const { active, over } = event;
    finishDrag();
    if (!over || active.id === over.id) {
      return;
    }
    const oldId = String(active.id);
    const newId = String(over.id);
    updateLocalSchema(oldId, newId);
    setTimeout(() => {
      updateWorkspaceSchema?.(oldId, newId);
    }, 0);
  }

  function handleDragStart (event: DragStartEvent) {
    scheduler?.setPaused(true);
    setActiveId(event.active.id);
    const rect = event.active.rect.current.initial;
    setDragSize(rect ? { width: rect.width, height: rect.height } : undefined);
  }

  const isDesignMode = workspaceState?.isMode('design') ?? false;
  const isAutosaveEnabled = workspaceState?.autosave ?? false;
  const activeItem = localItems.find((item) => item.name === activeId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragCancel={finishDrag}
      modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
    >
      <SortableContext
        items={localItems.map((item) => ({ id: item.name ?? '' }))}
        strategy={verticalListSortingStrategy}
      >
        <Suspense fallback={<Loader />}>
          {localItems.map((item, position) => (
            <SortableItemLazy
              key={item.name}
              item={item}
              isFirst={position === 0}
              isDesignMode={isDesignMode}
              isAutosaveEnabled={isAutosaveEnabled}
            />
          ))}
        </Suspense>
      </SortableContext>
      <DragOverlay>
        {activeItem?.type === 'mpiprocedure' ? (
          <div
            className="fb-sortable-drag"
            style={{ ...dragSize, minHeight: dragSize ? undefined : 160, padding: 16, boxSizing: 'border-box', background: 'white', border: '1px solid #ccc' }}
          >
            {translate('form.builder.step.number', { number: activeItem.stepIndex ?? 0 })}
          </div>
        ) : activeItem && (
          <SortablePlaceholder
            isFirst
            item={activeItem}
            isDesignMode={isDesignMode}
            isAutosaveEnabled={isAutosaveEnabled}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default withFBSortable(FBSortable);
